import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconsSend from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { ApolloError } from '@apollo/client';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import { ITranslationSubTypeInput } from '../../../../../../graphql/type/_inputTypes/translation';
import useLanguage from '../../../../../components/_hook/useLanguage';
import useNotification from '../../../../../components/_hook/useNotification';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import InputTranslationTextarea from '../../../../../components/_input/InputTranslation/InputTranslationTextarea';
import useForm from '../../../../../components/_hook/useForm';
import { useCreateCompanyProductCustomFieldMutation } from '../../../../../../graphql/mutation/company/createCompanyProductCustomFieldMutation';
import InputTranslationTextField from '../../../../../components/_input/InputTranslation/InputTranslationTextField';
import Context from '../../../../../context';
import { useCompanyQuery } from '../../../../../../graphql/query/company/useCompanyQuery';
import NotFound from '../../../../../components/_error/NotFound';
import { useUpdateCompanyProductCustomFieldMutation } from '../../../../../../graphql/mutation/company/updateCompanyProductCustomFieldMutation';
import Select, {
  IValue,
} from '../../../../../components/_select/_module/Select';

interface IProps {
  onSubmit?: () => void;
  onCancel: () => void;
  ramificationId?: string;
}

export default function FormCreateUpdateProductRamification(props: IProps) {
  interface IFormValue {
    title: ITranslationSubTypeInput[];
    description: ITranslationSubTypeInput[];
    dataType: string;
    fieldType: string;
    fieldAttributes: string[];
  }

  const { t } = useTranslation();
  const { company: contextCompany } = useContext(Context);
  const { getText } = useLanguage();
  const { onSubmit, ramificationId, onCancel } = props;
  const { notification } = useNotification();

  const companyFragment = `
            _id
            custom_fields {
              id
              locked
              title {
                la
                text
              }
              description {
                la
                text
              }
              field_type
              data_type
              attributes_input
              attributes
              currency
            }
  `;

  function onCompleted() {
    notification.success(t('success'));
    if (onSubmit) {
      onSubmit();
    }
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const { data: companyData, loading: companyLoading } = useCompanyQuery(
    companyFragment,
    {
      variables: { filter: { _id: contextCompany._id as string } },
      skip: !ramificationId,
    }
  );
  const [
    createCompanyProductCustomFieldMutation,
    { loading: isCreatingCompanyProductCustomField },
  ] = useCreateCompanyProductCustomFieldMutation(
    `updatedCompany {
				${companyFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  const [
    updateCompanyProductCustomFieldMutation,
    { loading: isUpdatingCompanyProductCustomField },
  ] = useUpdateCompanyProductCustomFieldMutation(
    `updatedCompany {
				${companyFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (companyData && ramificationId) {
      await updateCompanyProductCustomFieldMutation({
        variables: {
          companyId: contextCompany._id,
          productCustomFieldId: ramificationId,
          title: tempValues.title,
          description: tempValues.description,
          dataType: tempValues.dataType,
          fieldType: tempValues.fieldType,
          fieldAttributes: tempValues.fieldAttributes,
        },
      });
    } else {
      await createCompanyProductCustomFieldMutation({
        variables: {
          companyId: contextCompany._id,
          title: tempValues.title,
          description: tempValues.description,
          dataType: tempValues.dataType,
          fieldType: tempValues.fieldType,
          fieldAttributes: tempValues.fieldAttributes,
        },
      });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    isSubmitting,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      title: [],
      description: [],
      dataType: 'string',
      fieldType: 'input',
      fieldAttributes: [],
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.title.length || !getText(tempValues.title)) {
        newErrors.title = t('__name__is_required', {
          name: t('title'),
        });
      }

      if (!tempValues.dataType) {
        newErrors.dataType = t('__name__is_required', {
          name: t('data_type'),
        });
      }

      if (!tempValues.fieldType) {
        newErrors.fieldType = t('__name__is_required', {
          name: t('field_type'),
        });
      }

      return newErrors;
    },
  });

  useEffect(() => {
    if (!companyLoading && companyData && companyData.companies.items.length) {
      const company = companyData.companies.items[0];
      const ramification = company.custom_fields.find(
        (custom_field) => custom_field.id === ramificationId
      );
      if (ramification) {
        setValues({
          title: ramification.title.map((tempTitle) => ({
            text: tempTitle.text,
            la: tempTitle.la,
          })),
          description: ramification.description
            ? ramification.description.map((tempDescription) => ({
                text: tempDescription.text,
                la: tempDescription.la,
              }))
            : [],
          dataType: ramification.data_type,
          fieldType: ramification.field_type,
          fieldAttributes: ramification.attributes || [],
        });
      }
    }
  }, [companyLoading]);

  let ramification;
  if (ramificationId) {
    if (companyLoading) {
      return (
        <Grid container justifyContent="center">
          <CircularProgress size={30} color="inherit" />
        </Grid>
      );
    }

    const company = companyData ? companyData.companies.items[0] : null;

    if (!company) {
      return <NotFound />;
    }

    ramification = company.custom_fields.find(
      (custom_field) => custom_field.id === ramificationId
    );

    if (!ramification) {
      return <NotFound />;
    }
  }

  return (
    <>
      <DialogTitle>
        {ramification ? (
          <>
            {t('update')} {getText(ramification.title)} {t('ramification')}
          </>
        ) : (
          <>
            {t('create')} {t('ramification')}
          </>
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <InputTranslationTextField
              fullWidth
              required
              label={t('title')}
              values={values.title}
              onChange={(tempValues) => setValue('title', tempValues)}
              error={Boolean(errors.title)}
              helperText={errors.title}
            />
          </Grid>
          <Grid item xs={12}>
            <InputTranslationTextarea
              variant="outlined"
              fullWidth
              required
              label={t('description')}
              values={values.description}
              onChange={(tempValues) => setValue('description', tempValues)}
              error={Boolean(errors.description)}
              helperText={errors.description}
            />
          </Grid>

          {(!ramification ||
            (ramification.id !== 'prices' && ramification.id !== 'sku')) && (
            <>
              <Grid item xs={12}>
                <Select<IValue>
                  isClearable
                  options={[
                    {
                      label: t('string'),
                      value: 'string',
                    },
                    {
                      label: t('number'),
                      value: 'number',
                    },
                  ]}
                  label={t('data_type')}
                  fullWidth
                  menuPosition="fixed"
                  value={values.dataType}
                  onChange={(option) =>
                    setValue('dataType', option ? option.value : '')
                  }
                  error={Boolean(errors.dataType)}
                  helperText={errors.dataType}
                />
              </Grid>
              <Grid item xs={12}>
                <Select<IValue>
                  isClearable
                  options={[
                    {
                      label: t('input'),
                      value: 'input',
                    },
                    {
                      label: t('dropdown'),
                      value: 'dropdown',
                    },
                  ]}
                  label={t('field_type')}
                  fullWidth
                  menuPosition="fixed"
                  value={values.fieldType}
                  onChange={(option) => {
                    setValue('fieldType', option ? option.value : '');

                    if (option && option.value === 'dropdown') {
                      setValue('fieldAttributes', ['']);
                    }
                  }}
                  error={Boolean(errors.fieldType)}
                  helperText={errors.fieldType}
                />
              </Grid>
              {values.fieldType === 'dropdown' && (
                <Grid container item xs={12} spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      {t('dropdown_attributes')}
                    </Typography>
                  </Grid>
                  {values.fieldAttributes.map((fieldAttribute, index) => (
                    <Grid item xs={12}>
                      <TextField
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        fullWidth
                        label={`${t('dropdown')} ${index + 1}`}
                        value={fieldAttribute}
                        onChange={(e) => {
                          const value = [...values.fieldAttributes];

                          value[index] = e.target.value;
                          setValue('fieldAttributes', value);
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => {
                                  const value = [...values.fieldAttributes];
                                  value.splice(index, 1);
                                  setValue('fieldAttributes', value);
                                }}
                                size="large"
                              >
                                <RemoveIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => {
                        const value = [...values.fieldAttributes, ''];
                        setValue('fieldAttributes', value);
                      }}
                      startIcon={<AddIcon />}
                    >
                      {`${t('add')} ${t('dropdown')}`}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <ButtonSubmit
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              isSubmitting={
                isCreatingCompanyProductCustomField ||
                isUpdatingCompanyProductCustomField
              }
              label={t('submit')}
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
